import React, {
  ChangeEvent,
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components/macro';
import { ChromePicker, CirclePicker, ColorResult } from 'react-color';
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  SortableItem,
  SortableList,
  Text,
  Textarea,
  VStack,
  Circle,
  Code,
  HStack,
  TextInput,
  Link,
  useDisclosure,
  Spinner,
  Tooltip,
  Popover,
} from '@televet/kibble-ui';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { url } from 'shared/utils/validation';
import {
  ConnectSettingsClinicWidgetRequestTypeFragment,
  GetFormTemplateContentDocument,
  GetFormTemplateContentQuery,
  GetFormTemplateContentQueryVariables,
  FormTemplateType,
  WidgetRequestType,
  useGetClinicSettingsConnectWidgetQuery,
  useGetClinicSettingsConnectWidgetRequestOptionsLazyQuery,
  useUpdateClinicSettingsConnectWidgetMutation,
} from 'shared/types/graphql';
import { colorContrast } from 'shared/utils';
import useLocationState from 'shared/hooks/useLocationState';
import { IOption } from 'shared/components/Dropdown';
import Switch from 'shared/components/Switch';
import { getDefaultRequestFormTemplate } from '../../utils/getDefaultRequestFormTemplate';
import RequestTypesModal, { RequestTypesModalFormValues } from '../../components/RequestTypesModal';
import RequestTypeDraggableRow, { RequestTypeActions } from '../../components/RequestTypeDraggableRow';
import { env } from 'env';
import SendToDeveloperModal from './SendToDeveloperModal';
import DeleteWidgetRequestTypeModal, { IDeleteWidgetRequestTypeProps } from './DeleteWidgetRequestTypeModal';
import { useApolloClient } from '@apollo/client';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import isEqual from 'lodash-es/isEqual';
import { WidgetRequestTypeLabels } from '@televet/shared-types/widgetRequest/WidgetRequestTypeLabels';
import { ZENDESK_ARTICLE_BASE_URL } from 'shared/hooks/useZendesk';
import { getEnvironment } from 'shared/utils/http';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsRequestWidgetEvents } from 'shared/providers/TrackingProvider/EventNames';

const environment = getEnvironment();
const SHIM_URL = env.REACT_APP_WIDGET_SHIM_URL || '';

const isProduction = env.REACT_APP_PRODUCTION === 'true';

enum ConnectWidgetUpdateField {
  welcomeMessage = 'welcomeMessage',
  primaryColor = 'primaryColor',
  secondaryColor = 'secondaryColor',
  whiteLabelUrls = 'whiteLabelUrls',
}

type CustomClinicWidgetSetting = {
  id: string;
  isEnabled?: boolean | null;
  welcomeMessage?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  whiteLabelUrls?: string[] | null;
  clinicWidgetRequestTypes: ConnectSettingsClinicWidgetRequestTypeFragment[];
};

interface IWidgetErrorState {
  [key: string]: string | null;
  clinicWidgetRequestTypes: string | null;
  whiteLabelUrls: string | null;
}

const ErrorText = ({ children }: { children: React.ReactNode }): JSX.Element => {
  return <Text variant="danger">{children}</Text>;
};

const WebsiteWidgetSettings = forwardRef(
  (
    {
      hasPermission,
      setHasUnsavedChange,
    }: {
      hasPermission?: boolean;
      setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ): JSX.Element => {
    // Custom hooks
    const { currentClinicId, isOttoTeamMember } = useClinicUser();
    const { toastMessagePop } = useCustomToast();
    const apolloClient = useApolloClient();
    const [{ editClinicWidgetRequestType }, setLocationState] = useLocationState();
    const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
    const { trackEvent } = useTracking();
    const [selectedRequestType, setSelectedRequestType] = useState<
      ConnectSettingsClinicWidgetRequestTypeFragment | undefined
    >(undefined);

    // Custom Modal Hooks
    const { onOpen: onRequestOpen, isOpen: isRequestOpen, onClose: onRequestClose } = useDisclosure();
    const { onOpen: onDeleteOpen, isOpen: isDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isSendToDevOpen, onOpen: onSendToDevOpen, onClose: onSendToDevClose } = useDisclosure();

    // Page States
    const [widgetSettingState, setWidgetSettingState] = useState<CustomClinicWidgetSetting>({
      id: '',
      welcomeMessage: '',
      clinicWidgetRequestTypes: [],
      isEnabled: false,
      primaryColor: 'var(--chakra-colors-background-primary)',
      secondaryColor: '#363546',
      whiteLabelUrls: [],
    });
    const [widgetErrorState, setWidgetErrorState] = useState<IWidgetErrorState>({
      welcomeMessage: null,
      clinicWidgetRequestTypes: null,
      whiteLabelUrls: null,
    });
    const [primaryColorPickerIsOpen, setPrimaryColorPickerIsOpen] = useState<boolean>(false);
    const [secondaryColorPickerIsOpen, setSecondaryColorPickerIsOpen] = useState<boolean>(false);
    const [isLoadingInitialWidgetSettings, setIsLoadingInitialWidgetSettings] = useState<boolean>(true);
    const [deleteModalProps, setDeleteModalProps] = useState<IDeleteWidgetRequestTypeProps>();
    const [connectWidgetSettingChanges, setConnectWidgetSettingChanges] = useState<ConnectWidgetUpdateField[]>([]);

    // Required Constants
    const whiteLabelUrls = widgetSettingState.whiteLabelUrls;
    const welcomeMessage = widgetSettingState.welcomeMessage;

    // UseMemo Sections
    const widgetEmbedScript = useMemo(() => {
      return `<!-- Start of Otto Website Widget embed script -->
  <script>
    (function (id, win, doc) {
      win.televet = win.televet || { id };
      win.otto = win.otto || { id };
      var o = doc.createElement('script');
      o.async = true;
      o.src = '${SHIM_URL}';
      var r = doc.getElementsByTagName('script')[0];
      r.parentNode.insertBefore(o, r);
    })('${currentClinicId}', window, document);
  </script>
<!-- End of Otto Website Widget embed script -->`;
    }, [currentClinicId]);

    // Graphql Query Hooks
    const { data: ClinicConnectWidgetSettings, refetch: refetchClinicConnectWidgetSettings } =
      useGetClinicSettingsConnectWidgetQuery({
        variables: {
          where: {
            id: currentClinicId,
          },
        },
        skip: !currentClinicId,
        errorPolicy: GraphQLErrorPolicies.All,
        fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      });

    const clinicWidgetSettingsData = ClinicConnectWidgetSettings?.findUniqueClinic?.clinicWidgetSetting;

    const setInitialData = useCallback(() => {
      if (clinicWidgetSettingsData) {
        const clinicWidgetRequestTypesCopy = [...clinicWidgetSettingsData.clinicWidgetRequestTypes];

        const sortedClinicWidgetRequestTypes = clinicWidgetRequestTypesCopy.sort((a, b) => {
          return a.order - b.order;
        });
        setWidgetSettingState({
          id: clinicWidgetSettingsData.id,
          welcomeMessage: clinicWidgetSettingsData.welcomeMessage,
          clinicWidgetRequestTypes: sortedClinicWidgetRequestTypes,
          isEnabled: clinicWidgetSettingsData.isEnabled,
          primaryColor: clinicWidgetSettingsData.primaryColor,
          secondaryColor: clinicWidgetSettingsData.secondaryColor,
          whiteLabelUrls: clinicWidgetSettingsData.whiteLabelUrls,
        });
        setIsLoadingInitialWidgetSettings(false);
        setHasUnsavedChange(false);
        setConnectWidgetSettingChanges([]);
      }
    }, [clinicWidgetSettingsData, setHasUnsavedChange]);

    const [getWidgetRequestOptionSettings] = useGetClinicSettingsConnectWidgetRequestOptionsLazyQuery({
      variables: {
        where: {
          id: currentClinicId,
        },
      },
      errorPolicy: GraphQLErrorPolicies.All,
      fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
      onCompleted: (data) => {
        setWidgetSettingState({
          ...widgetSettingState,
          clinicWidgetRequestTypes: data?.findUniqueClinic?.clinicWidgetSetting?.clinicWidgetRequestTypes || [],
        });
      },
    });

    const [updateClinic, { loading: updateClinicLoading }] = useUpdateClinicSettingsConnectWidgetMutation({
      errorPolicy: GraphQLErrorPolicies.All,
    });

    const handleListSort = (widgetRequestTypes: ConnectSettingsClinicWidgetRequestTypeFragment[]): void => {
      setWidgetSettingState({
        ...widgetSettingState,
        clinicWidgetRequestTypes: widgetRequestTypes,
      });

      updateClinic({
        variables: {
          where: {
            id: currentClinicId,
          },
          data: {
            clinicWidgetSetting: {
              update: {
                data: {
                  clinicWidgetRequestTypes: {
                    update: widgetRequestTypes.map((item, index) => {
                      return {
                        where: { id: item.id },
                        data: {
                          order: index,
                        },
                      };
                    }),
                  },
                },
              },
            },
          },
        },
      });
    };

    const onMenuItemSelected = async (e: MouseEvent, option: IOption, index: number): Promise<void> => {
      const clinicWidgetRequestTypesCopy = widgetSettingState?.clinicWidgetRequestTypes
        ? [...widgetSettingState.clinicWidgetRequestTypes]
        : [];
      const clinicWidgetRequestType = clinicWidgetRequestTypesCopy[index];

      if (option.value === RequestTypeActions.Edit) {
        openEditRequestOptionModal(clinicWidgetRequestType);
      } else if (option.value === RequestTypeActions.Delete) {
        setDeleteModalProps({
          clinicId: currentClinicId || '',
          clinicWidgetRequestType,
          onConfirm: () => {
            clinicWidgetRequestTypesCopy.splice(index, 1);
            setWidgetSettingState({
              ...widgetSettingState,
              clinicWidgetRequestTypes: clinicWidgetRequestTypesCopy,
            });
          },
        });
        onDeleteOpen();
      }
    };

    // Custom Handler Function
    // Auto Save
    const onWidgetEnabledToggle = (e: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      try {
        updateClinic({
          variables: {
            where: {
              id: currentClinicId,
            },
            data: {
              clinicWidgetSetting: {
                update: {
                  data: {
                    isEnabled: checked,
                  },
                },
              },
            },
          },
        });
        setWidgetSettingState({
          ...widgetSettingState,
          isEnabled: checked,
        });
        toastMessagePop();
      } catch (error) {
        toastMessagePop('There was an error while changing Widget setting', STATUSES.error);
      }
    };

    const onDeleteWhiteLabelClicked = (index: number): void => {
      try {
        const whiteLabelUrlsCopy = [...(widgetSettingState.whiteLabelUrls || [])];
        whiteLabelUrlsCopy.splice(index, 1);
        updateClinic({
          variables: {
            where: {
              id: currentClinicId,
            },
            data: {
              clinicWidgetSetting: {
                update: {
                  data: {
                    whiteLabelUrls: whiteLabelUrlsCopy,
                  },
                },
              },
            },
          },
        });
        setWidgetSettingState({
          ...widgetSettingState,
          whiteLabelUrls: whiteLabelUrlsCopy,
        });
        toastMessagePop();
        if (
          connectWidgetSettingChanges.includes(ConnectWidgetUpdateField.whiteLabelUrls) &&
          isEqual(whiteLabelUrlsCopy, clinicWidgetSettingsData?.whiteLabelUrls)
        ) {
          setHasUnsavedChange(false);
          setConnectWidgetSettingChanges([]);
        }
      } catch (error) {
        toastMessagePop('There was an error while changing Widget setting', STATUSES.error);
      }
    };

    const onPropertyToggleChanged = (
      e: ChangeEvent<HTMLInputElement>,
      checked: boolean,
      index: number,
      property: keyof Pick<ConnectSettingsClinicWidgetRequestTypeFragment, 'isActive' | 'shouldCreateTask'>,
    ): void => {
      const clinicWidgetRequestTypesCopy = widgetSettingState?.clinicWidgetRequestTypes
        ? [...widgetSettingState.clinicWidgetRequestTypes]
        : [];

      const clinicWidgetRequestType = clinicWidgetRequestTypesCopy[index];
      try {
        updateClinic({
          variables: {
            where: {
              id: currentClinicId,
            },
            data: {
              clinicWidgetSetting: {
                update: {
                  data: {
                    clinicWidgetRequestTypes: {
                      update: [
                        {
                          where: { id: clinicWidgetRequestType.id },
                          data: {
                            [property]: checked,
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
          },
        });

        if (property === 'isActive') {
          trackEvent(SettingsRequestWidgetEvents.EnableWidget, { on: checked });
        }
        clinicWidgetRequestTypesCopy[index] = {
          ...clinicWidgetRequestType,
          [property]: checked,
        };
        setWidgetSettingState({
          ...widgetSettingState,
          clinicWidgetRequestTypes: clinicWidgetRequestTypesCopy,
        });
      } catch (error) {
        toastMessagePop('There was an error while changing Widget setting', STATUSES.error);
      }
    };

    const handleRequestTypesModalSubmit = async (values: RequestTypesModalFormValues): Promise<void> => {
      const assigneeIds = values.assigneeIds || [];
      const clinicWidgetRequestTypes = widgetSettingState?.clinicWidgetRequestTypes || [];

      // Don't set form template content for direct booking
      let formTemplateContent;
      if (values.requestType !== WidgetRequestType.DirectBooking) {
        if (values.formTemplateId) {
          const result = await apolloClient.query<GetFormTemplateContentQuery, GetFormTemplateContentQueryVariables>({
            query: GetFormTemplateContentDocument,
            variables: { formTemplateId: values.formTemplateId },
          });

          formTemplateContent = result.data.findUniqueFormTemplate?.content;
        } else {
          formTemplateContent = getDefaultRequestFormTemplate(values.requestType);
        }
      }

      // Don't create or connect a form template for direct booking
      let formTemplate;
      if (values.requestType !== WidgetRequestType.DirectBooking) {
        formTemplate = values.formTemplateId
          ? { connect: { id: values.formTemplateId } }
          : {
            create: {
              title: `${values.displayName || WidgetRequestTypeLabels[values.requestType]}`,
              clinic: { connect: { id: currentClinicId } },
              content: formTemplateContent,
              isActive: true,
              formTemplateType: FormTemplateType.WidgetRequest,
            },
          };
      }

      if (values.requestTypeAction === RequestTypeActions.Add) {
        await updateClinic({
          variables: {
            where: {
              id: currentClinicId,
            },
            data: {
              clinicWidgetSetting: {
                update: {
                  data: {
                    clinicWidgetRequestTypes: {
                      create: [
                        {
                          isActive: true,
                          isDeleted: false,
                          order: clinicWidgetRequestTypes.length,
                          displayName: values.displayName,
                          requestType: values.requestType,
                          assignees: {
                            connect: assigneeIds.map((id) => {
                              return { id };
                            }),
                          },
                          channelStatus: { connect: { id: values.channelStatusId } },
                          formTemplate: formTemplate,
                          requestVisibility: values.requestVisibility,
                        },
                      ],
                    },
                  },
                },
              },
            },
          },
        }).then(() => {
          getWidgetRequestOptionSettings();
        });
      } else {
        // Disconnect assignees that have been removed
        const requestType = clinicWidgetRequestTypes.find((r) => r.id === values.requestTypeId);
        const disconnectAssignees = requestType?.assignees?.filter((a) => !assigneeIds.includes(a.id)) || [];
        await updateClinic({
          variables: {
            where: {
              id: currentClinicId,
            },
            data: {
              clinicWidgetSetting: {
                update: {
                  data: {
                    clinicWidgetRequestTypes: {
                      update: [
                        {
                          where: { id: values.requestTypeId },
                          data: {
                            displayName: values.displayName,
                            requestType: values.requestType,
                            assignees: {
                              connect: assigneeIds.map((id) => {
                                return { id };
                              }),
                              disconnect: disconnectAssignees.map((assignee) => {
                                return { id: assignee.id };
                              }),
                            },
                            channelStatus: { connect: { id: values.channelStatusId } },
                            formTemplate: values.formTemplateId
                              ? {
                                connect: { id: values.formTemplateId },
                              }
                              : values.requestType !== WidgetRequestType.DirectBooking
                                ? {
                                  create: {
                                    title: `${values.displayName || WidgetRequestTypeLabels[values.requestType]}`,
                                    clinic: { connect: { id: currentClinicId } },
                                    content: formTemplateContent,
                                    isActive: true,
                                    formTemplateType: FormTemplateType.WidgetRequest,
                                  },
                                }
                                : undefined,
                            requestVisibility: values.requestVisibility,
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
          },
        }).then(() => {
          getWidgetRequestOptionSettings();
        });
      }

      onRequestClose();
    };

    const isFormValid = (): boolean => {
      const { whiteLabelUrls, clinicWidgetRequestTypes } = widgetSettingState;

      setWidgetErrorState({
        clinicWidgetRequestTypes: null,
        whiteLabelUrls: null,
      });

      const invalidWhiteLabelUrlsValid = whiteLabelUrls?.filter((urlString: string) => {
        return !url.test(urlString);
      });

      if (
        invalidWhiteLabelUrlsValid?.length ||
        whiteLabelUrls?.length === 0 ||
        (clinicWidgetRequestTypes && clinicWidgetRequestTypes?.length < 1)
      ) {
        setWidgetErrorState({
          clinicWidgetRequestTypes:
            clinicWidgetRequestTypes && clinicWidgetRequestTypes?.length < 1
              ? 'You must have at least one request option configured.'
              : null,
          whiteLabelUrls:
            invalidWhiteLabelUrlsValid?.length || whiteLabelUrls?.length === 0
              ? 'Please enter a valid URL to whitelist. (e.g. https://www.example.com)'
              : null,
        });
        return false;
      }

      return true;
    };

    const onConnectSettingWidgetUpdate = async (): Promise<void> => {
      const { welcomeMessage, primaryColor, secondaryColor, whiteLabelUrls } = widgetSettingState;
      try {
        if (!isFormValid()) {
          let errorMessage = 'There was a problem updating your widget settings.';
          for (const prop in widgetErrorState) {
            if ((widgetErrorState as IWidgetErrorState)[prop] !== null) {
              errorMessage += ` ${(widgetErrorState as IWidgetErrorState)[prop]}`;
            }
          }
          toastMessagePop(errorMessage, STATUSES.error);
          return;
        }

        await updateClinic({
          variables: {
            where: {
              id: currentClinicId,
            },
            data: {
              clinicWidgetSetting: {
                update: {
                  data: {
                    welcomeMessage,
                    primaryColor,
                    secondaryColor,
                    whiteLabelUrls,
                  },
                },
              },
            },
          },
        });

        trackEvent(SettingsRequestWidgetEvents.SaveSettingClicked);
        toastMessagePop('Your widget settings have been updated.');
        refetchClinicConnectWidgetSettings();
        onResetPage();
      } catch (e) {
        Sentry.captureException(e);
        toastMessagePop('Your widget settings have been updated.', STATUSES.error);
      }
    };

    const onAddWhiteLabelUrl = (): void => {
      const whiteLabelUrlsCopy = [...(widgetSettingState.whiteLabelUrls || []), ''];
      const newState = {
        ...widgetSettingState,
        whiteLabelUrls: whiteLabelUrlsCopy,
      };
      setWidgetSettingState(newState);
    };

    const onWidgetSettingChange = useCallback(
      (value: string | ColorResult, field: ConnectWidgetUpdateField, index?: number): void => {
        let whiteLabelUrlsCopy: string[] = [];
        if (field === ConnectWidgetUpdateField.whiteLabelUrls) {
          if (index !== undefined && typeof value === 'string') {
            whiteLabelUrlsCopy = [...(widgetSettingState.whiteLabelUrls || [])];
            whiteLabelUrlsCopy[index] = value;
          }
        }
        const newState = {
          ...widgetSettingState,
          [field]: whiteLabelUrlsCopy.length > 0 ? whiteLabelUrlsCopy : value,
        };
        setWidgetSettingState(newState);

        trackEvent(SettingsRequestWidgetEvents.UpdateWidgetSettings, { field });
        if (!isEqual(newState[field], clinicWidgetSettingsData?.[field])) {
          setHasUnsavedChange(true);
          setConnectWidgetSettingChanges((prevVal) => {
            const newVal = prevVal || [];
            if (newVal.includes(ConnectWidgetUpdateField[field]) === false) {
              return [...newVal, ConnectWidgetUpdateField[field]];
            }
            return newVal;
          });
        } else if (isEqual(newState[field], clinicWidgetSettingsData?.[field])) {
          const newState = connectWidgetSettingChanges?.filter(
            (clinicHourUpdateField) => clinicHourUpdateField !== ConnectWidgetUpdateField[field],
          );
          setConnectWidgetSettingChanges(() => {
            return newState || [];
          });
          if (!!newState) {
            setHasUnsavedChange(false);
          }
        }
      },
      [clinicWidgetSettingsData, connectWidgetSettingChanges, setHasUnsavedChange, trackEvent, widgetSettingState],
    );

    const onCopyWidgetSnippet = (): void => {
      navigator.clipboard.writeText(widgetEmbedScript);
      trackEvent(SettingsRequestWidgetEvents.CopyClicked);
    };

    // UseCallback Section
    const openEditRequestOptionModal = useCallback(
      (clinicWidgetRequestType: ConnectSettingsClinicWidgetRequestTypeFragment): void => {
        onRequestOpen();
        setSelectedRequestType(clinicWidgetRequestType);
      },
      [onRequestOpen],
    );

    const onSendToDeveloperClick = useCallback(() => {
      trackEvent(SettingsRequestWidgetEvents.SendWebDeveloperClicked);
      onSendToDevOpen();
    }, [onSendToDevOpen, trackEvent]);

    const onResetPage = useCallback((): void => {
      setInitialData();
      setHasUnsavedChange(false);
      setConnectWidgetSettingChanges([]);
    }, [setHasUnsavedChange, setInitialData]);

    useImperativeHandle(ref, () => ({
      onLeavingCurrentPage(): void {
        setConnectWidgetSettingChanges([]);
      },
      onClickedSaveButton(): void {
        onConnectSettingWidgetUpdate();
      },
      onClickedCancelButton(): void {
        onResetPage();
      },
    }));

    // UseEffect section
    useEffect(() => {
      isFormValid();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [whiteLabelUrls, welcomeMessage]);

    useEffect(() => {
      const types = widgetSettingState?.clinicWidgetRequestTypes;
      if (editClinicWidgetRequestType && types?.length) {
        const clinicWidgetRequestType = types.find(({ id }) => id === editClinicWidgetRequestType);
        if (clinicWidgetRequestType) openEditRequestOptionModal(clinicWidgetRequestType);
        setLocationState({ editClinicWidgetRequestType: null });
      }
    }, [editClinicWidgetRequestType, widgetSettingState, openEditRequestOptionModal, setLocationState]);

    useEffect(() => {
      onResetPage();
    }, [onResetPage]);

    const openWidgetPreviewWindow = useCallback(() => {
      if (!window) return;
      let url = `https://widget.${environment ? environment : 'test'}.dev.otto.vet/usage.html?id=`;

      if (isProduction) {
        url = 'https://connect.televet.com/preview?id=';
      }

      window.open(`${url}${currentClinicId}`, '_blank');
    }, [currentClinicId]);

    if (isLoadingInitialWidgetSettings) {
      return (
        <Flex w="100%" h="100%" bg="background.subtle" align="center" justify="center">
          <Spinner size="lg" />
        </Flex>
      );
    }

    return (
      <Box zIndex={0}>
        <RequestTypesModal
          clinicWidgetRequestType={selectedRequestType}
          isOpen={isRequestOpen}
          onCancel={onRequestClose}
          isLoading={updateClinicLoading}
          onSubmit={handleRequestTypesModalSubmit}
        />
        <SendToDeveloperModal isOpen={isSendToDevOpen} onClose={onSendToDevClose} />
        <DeleteWidgetRequestTypeModal isOpen={isDeleteOpen} onClose={onDeleteClose} {...deleteModalProps} />
        <Flex justify="space-between" align="center" my={5} ml="10px">
          <Text size="sm" w="50%">
            The Website Widget is a customizable add-on for your website that collects requests for services from new
            and existing clients. Please note that the script (available below) must be live on your site for the widget
            to appear.
          </Text>
          {isOttoTeamMember && (
            <Button size="sm" onClick={openWidgetPreviewWindow}>
              Preview Widget
            </Button>
          )}
        </Flex>
        <Divider my={5} />
        <Box mx={4}>
          <HStack mb={2} align="center">
            <Text fontWeight="bold">Enable your Website Widget </Text>
            <Tooltip label="When the toggle is set to 'on', your Website Widget will appear on your website. Turn the toggle off to remove the Website Widget from your website.">
              <Box>
                <Icon name="infoCircle" size="xs" variant="subtle" />
              </Box>
            </Tooltip>
          </HStack>
          <Switch
            hasPermission={hasPermission}
            name="widgetEnabled"
            label={
              hasPermission
                ? ''
                : !widgetSettingState.isEnabled
                  ? 'Website Widget is disabled'
                  : 'Website Widget is enabled'
            }
            checked={widgetSettingState.isEnabled || false}
            onChange={onWidgetEnabledToggle}
          />
        </Box>
        <Divider my={5} />

        <Box mx={4}>
          <HStack mb={2} align="center">
            <Text fontWeight="bold">Welcome message</Text>
            <Tooltip label="When the toggle is set to 'on', your Website Widget will appear on your website. Turn the toggle off to remove the Website Widget from your website.">
              <Box>
                <Icon name="infoCircle" size="xs" variant="subtle" />
              </Box>
            </Tooltip>
          </HStack>

          <Textarea
            w="500px"
            resize="none"
            mt={3}
            h="100px"
            isDisabled={!hasPermission}
            borderColor="border.default"
            value={widgetSettingState.welcomeMessage || ''}
            onChange={(e): void => onWidgetSettingChange(e.target.value, ConnectWidgetUpdateField.welcomeMessage)}
            onBlur={(): void => {
              trackEvent(SettingsRequestWidgetEvents.WelcomeMessage, {
                welcomeMessage: widgetErrorState.welcomeMessage || '',
              });
            }}
          />
          <br />
          {hasPermission && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(): void =>
                onWidgetSettingChange('How can we help you?', ConnectWidgetUpdateField.welcomeMessage)
              }
              mt="3"
              mb="4"
            >
              Reset to Default
            </Button>
          )}
        </Box>
        <Divider my={5} />

        <Box mx={4}>
          <>
            <Flex justify="space-between">
              <HStack mb={2} align="center">
                <Text fontWeight="bold"> Request options</Text>
                <Tooltip label="The request options that will appear on your Website Widget are below. Click the 'Add New' button to update your list of requests.">
                  <Box>
                    <Icon name="infoCircle" size="xs" variant="subtle" />
                  </Box>
                </Tooltip>
              </HStack>

              {hasPermission && (
                <Button
                  iconName="plus"
                  size="sm"
                  variant="secondary"
                  onClick={(): void => {
                    onRequestOpen();
                    setSelectedRequestType(undefined);
                    trackEvent(SettingsRequestWidgetEvents.AddNewRequest);
                  }}
                >
                  Add New
                </Button>
              )}
            </Flex>
            {widgetErrorState.clinicWidgetRequestTypes && (
              <ErrorText>{widgetErrorState.clinicWidgetRequestTypes}</ErrorText>
            )}
            <>
              {(widgetSettingState?.clinicWidgetRequestTypes?.length || -1) > 0 && (
                <>
                  <Flex justify="space-between" py={3} px={2} gap={2}>
                    <Box w={8} flex="0 0 auto" />
                    <Text align="left" size="xs" textTransform="uppercase" w="220px" flex="0 1 auto">
                      Request Name
                    </Text>
                    <Text size="xs" flex={1} textTransform="uppercase">
                      Icon
                    </Text>
                    <HStack>
                      <Text size="xs" flex={1} textTransform="uppercase">
                        Conversation Status{' '}
                      </Text>
                      <Tooltip label="The associated conversation will be set to this status when a widget request of this type is made.">
                        <Box>
                          <Icon name="infoCircle" size="xs" variant="subtle" />
                        </Box>
                      </Tooltip>
                    </HStack>
                    <Text size="xs" flex={1} textTransform="uppercase">
                      Assign to
                    </Text>
                    <Text size="xs" flex={1} textTransform="uppercase">
                      Active
                    </Text>
                    {!isFeatureFlagsLoading && isFeatureEnabled(FeatureFlagName.Tasks) && (
                      <Text size="xs" flex={1} textTransform="uppercase" data-testid="add-to-tasks-column-header">
                        Create Task
                      </Text>
                    )}
                    <Box w={8} />
                  </Flex>
                  <Divider />
                  <>
                    <SortableList
                      items={widgetSettingState?.clinicWidgetRequestTypes}
                      onChange={(widgetRequestTypes): void => handleListSort(widgetRequestTypes)}
                      renderItem={(widgetRequestType, index): JSX.Element => (
                        <SortableItem
                          id={widgetRequestType.id}
                          style={{
                            padding: 2,
                            background:
                              (index && index % 2 === 0) || index === 0 ? 'background.default' : 'background.subtle',
                          }}
                        >
                          <RequestTypeDraggableRow
                            hasPermission={hasPermission}
                            key={widgetRequestType.id}
                            widgetRequestType={widgetRequestType}
                            index={index}
                            onPropertyToggleChanged={onPropertyToggleChanged}
                            onMenuItemSelected={onMenuItemSelected}
                            displayTaskCreation={
                              [
                                WidgetRequestType.TalkToStaff,
                                WidgetRequestType.InboundSms,
                                WidgetRequestType.DirectBooking,
                                WidgetRequestType.Teletriage,
                              ].includes(widgetRequestType.requestType) === false
                            }
                          />
                        </SortableItem>
                      )}
                    />
                  </>
                </>
              )}
            </>
          </>
        </Box>
        <Divider my={5} />

        <Box mx={4}>
          <Heading size="sm" mb={2}>
            Clinic colors
          </Heading>
          <Flex mt={5}>
            {hasPermission && (
              <Flex direction="column" align="stretch">
                <Heading size="xs" mb={2}>
                  Primary Color
                </Heading>
                <Box position="relative" mt={2}>
                  <CirclePicker
                    colors={[
                      '#d3273e',
                      '#dc582a',
                      '#ffc845',
                      '#75c653',
                      '#00bfb2',
                      '#41b6e6',
                      '#1d4289',
                      '#852cdd',
                      '#000000',
                    ]}
                    color={widgetSettingState.primaryColor || 'var(--chakra-colors-background-primary)'}
                    onChange={(e): void => onWidgetSettingChange(e.hex, ConnectWidgetUpdateField.primaryColor)}
                    circleSize={22}
                    circleSpacing={10}
                    width="170px"
                  />
                  <Circle
                    bgColor={widgetSettingState.primaryColor || 'var(--chakra-colors-background-primary)'}
                    color={widgetSettingState.primaryColor ? colorContrast(widgetSettingState.primaryColor) : '#000'}
                    onClick={(): void => setPrimaryColorPickerIsOpen(!primaryColorPickerIsOpen)}
                    cursor="pointer"
                    position="absolute"
                    left="128px"
                    top="32px"
                    size="22px"
                  >
                    <Icon name="eyedropper" size="sm" color="currentColor" />
                  </Circle>
                  {primaryColorPickerIsOpen && (
                    <Box left={165} bottom={-85} pos="absolute">
                      <Box
                        pos="fixed"
                        top={0}
                        bottom={0}
                        left={0}
                        right={0}
                        onClick={(): void => setPrimaryColorPickerIsOpen(false)}
                      />
                      <ChromePicker
                        color={widgetSettingState.primaryColor || 'var(--chakra-colors-background-primary)'}
                        onChange={(e): void => onWidgetSettingChange(e.hex, ConnectWidgetUpdateField.primaryColor)}
                      />
                    </Box>
                  )}
                </Box>
              </Flex>
            )}
            <Flex direction="column" ml={hasPermission ? '35px' : '0px'}>
              <Heading size="xs" mb={2}>
                Preview
              </Heading>
              <PrimaryPreviewButton
                hexColor={widgetSettingState.primaryColor || 'var(--chakra-colors-background-primary)'}
              >
                Submit Request
              </PrimaryPreviewButton>
            </Flex>
          </Flex>
          <Flex mt={5}>
            {hasPermission && (
              <Flex direction="column" align="stretch">
                <Heading size="xs" mb={2}>
                  Secondary Color
                </Heading>
                <Box position="relative" mt={2}>
                  <CirclePicker
                    colors={[
                      '#d3273e',
                      '#dc582a',
                      '#ffc845',
                      '#75c653',
                      '#00bfb2',
                      '#41b6e6',
                      '#1d4289',
                      '#852cdd',
                      '#000000',
                    ]}
                    color={widgetSettingState.secondaryColor || '#363546'}
                    onChange={(e): void => onWidgetSettingChange(e.hex, ConnectWidgetUpdateField.secondaryColor)}
                    circleSize={22}
                    circleSpacing={10}
                    width="170px"
                  />
                  <Circle
                    bgColor={widgetSettingState.secondaryColor || '#363546'}
                    color={
                      widgetSettingState.secondaryColor ? colorContrast(widgetSettingState.secondaryColor) : '#000'
                    }
                    onClick={(): void => setSecondaryColorPickerIsOpen(!secondaryColorPickerIsOpen)}
                    cursor="pointer"
                    position="absolute"
                    left="128px"
                    top="32px"
                    size="22px"
                  >
                    <Icon name="eyedropper" size="sm" color="currentColor" />
                  </Circle>
                  {secondaryColorPickerIsOpen && (
                    <Box left={165} bottom={-85} position="absolute">
                      <Box
                        position="fixed"
                        top={0}
                        bottom={0}
                        left={0}
                        right={0}
                        onClick={(): void => setSecondaryColorPickerIsOpen(false)}
                      />
                      <ChromePicker
                        color={widgetSettingState.secondaryColor || '#363546'}
                        onChange={(e): void => onWidgetSettingChange(e.hex, ConnectWidgetUpdateField.secondaryColor)}
                      />
                    </Box>
                  )}
                </Box>
              </Flex>
            )}
            <Flex direction="column" ml={hasPermission ? '35px' : '0px'}>
              <Heading size="xs" mb={2}>
                Preview
              </Heading>
              <SecondaryPreviewBanner hexColor={widgetSettingState.secondaryColor || '#363546'}>
                Common Requests
              </SecondaryPreviewBanner>
            </Flex>
          </Flex>
        </Box>
        <Divider my={5} />

        {hasPermission && (
          <>
            <Box mx={4}>
              <Heading size="sm" mb={2}>
                Website Widget Script
              </Heading>
              <Text size="sm" w="50%">
                <div>
                  Add the following code just above the <code>&lt;/body&gt;</code> HTML tag to include the Website
                  Widget on your website.
                </div>

                <Link
                  href={`${ZENDESK_ARTICLE_BASE_URL}/4410172217101-Add-the-Connect-Widget-to-Your-Website`}
                  target="_blank"
                  rel="noopener noreferrer"
                  display="inline-block"
                  mt={3}
                  mb={4}
                  size="sm"
                >
                  View Installation Instructions
                </Link>
              </Text>

              <VStack align="flex-end" gap={4} maxW="500px">
                <Code
                  whiteSpace="pre"
                  w="100%"
                  p={3}
                  bg="background.info"
                  borderWidth={1}
                  borderRadius={8}
                  variant="subtle"
                  borderColor="border.default"
                >
                  {widgetEmbedScript}
                </Code>
                <HStack justify="flex-end">
                  <Popover
                    size="xs"
                    anchor={
                      <Button iconName="twoSquares" variant="secondary" size="sm" onClick={onCopyWidgetSnippet}>
                        Copy
                      </Button>
                    }
                  >
                    Copied to clipboard!
                  </Popover>
                  <Button iconName="send" variant="secondary" size="sm" onClick={onSendToDeveloperClick}>
                    Send to Web Developer
                  </Button>
                </HStack>
              </VStack>
            </Box>
            <Divider my={5} />
          </>
        )}

        {hasPermission && (
          <>
            <Box mx={4}>
              <HStack>
                <Text fontWeight="bold">Whitelist URLs</Text>
                <Tooltip label="Add URLs that your Website Widget can appear on. This will ensure that the Website Widget only appears on web pages you authorize.">
                  <Box>
                    <Icon name="infoCircle" size="xs" variant="subtle" />
                  </Box>
                </Tooltip>
              </HStack>

              {widgetErrorState.whiteLabelUrls && <ErrorText>{widgetErrorState.whiteLabelUrls}</ErrorText>}
              <Box w="400px" mt={5}>
                {!!widgetSettingState.whiteLabelUrls &&
                  widgetSettingState.whiteLabelUrls?.map((whiteLabelUrl: string, index: number) => {
                    return (
                      <Flex justify="space-between" gap={2} mb={4} key={index}>
                        <TextInput
                          size="sm"
                          name={String(index)}
                          value={whiteLabelUrl}
                          onChange={(e): void =>
                            onWidgetSettingChange(e.target.value, ConnectWidgetUpdateField.whiteLabelUrls, index)
                          }
                          onBlur={(): void => {
                            trackEvent(SettingsRequestWidgetEvents.WhiteLabelUrls);
                          }}
                        />
                        <Button
                          size="sm"
                          variant="tertiary"
                          iconName="trashcan"
                          onClick={(): void => onDeleteWhiteLabelClicked(index)}
                        />
                      </Flex>
                    );
                  })}
              </Box>
              <Button
                iconName="plus"
                size="sm"
                variant="secondary"
                onClick={onAddWhiteLabelUrl}
                disabled={updateClinicLoading}
              >
                Add URL
              </Button>
            </Box>
            <Divider my={5} />
          </>
        )}
      </Box>
    );
  },
);

WebsiteWidgetSettings.displayName = 'WebsiteWidgetSettings';

export default WebsiteWidgetSettings;

const SecondaryPreviewBanner = styled.div<{ hexColor: string }>`
  background-color: ${({ hexColor }): string => {
    return hexColor || '#fff';
  }};
  color: ${({ hexColor }): string => {
    return hexColor ? colorContrast(hexColor) : '#000';
  }};
  font-size: 22px;
  font-weight: 600;
  padding: 16px;
  margin: 8px 0;
  width: 314px;
  flex: 0 1 auto;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PrimaryPreviewButton = styled.button<{ hexColor: string }>`
  background-color: ${({ hexColor }): string => {
    return hexColor || '#fff';
  }};
  border: none;
  border-radius: 9px;
  color: ${({ hexColor }): string => {
    return hexColor ? colorContrast(hexColor) : '#000';
  }};

  font-size: 14px;
  font-weight: 600;
  height: 36px;
  padding: 0 16px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
